import "../../../../styles/css/report.css"
import React, { forwardRef, useRef, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler, CategoryScale, LinearScale, Title, BarElement } from "chart.js";
import { Line } from "react-chartjs-2";
import { useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment";
import { processReportImagesForJunior } from "../../../../utils/maskingHelper";
import Cover from "../../organisms/Cover";
import UserInfo from "../../organisms/UserInfo";
import SkinTone from "../../organisms/SkinTone";
import SkinAging from "../../organisms/SkinAging";
import SkinTrouble from "../../organisms/SkinTrouble";
import CharacteristicList from "../../organisms/CharacteristicList";
import MeasurementInfo from "../../organisms/MeasurementInfo";
import DetailSkinGlow from "../../organisms/DetailSkinGlow";
import DetailBlackhead from "../../organisms/DetailBlackhead";
import DetailPore from "../../organisms/DetailPore";
import DetailSpeckle from "../../organisms/DetailSpeckle";
import DetailSensitive from "../../organisms/DetailSensitive";
import DetailAcne from "../../organisms/DetailAcne";
import DetailForeheadLine from "../../organisms/DetailForeheadLine";
import DetailDarkCircle from "../../organisms/DetailDarkCircle";
import DetailFrownLine from "../../organisms/DetailFrownLine";
import DetailUnderEyeArea from "../../organisms/DetailUnderEyeArea";
import DetailTearTrough from "../../organisms/DetailTearTrough";
import DetailPtosis from "../../organisms/DetailPtosis";
import DetailWrinkleUnderEye from "../../organisms/DetailWrinkleUnderEye";
import DetailCrowFeet from "../../organisms/DetailCrowFeet";
import DetailAppleZone from "../../organisms/DetailAppleZone";
import DetailPurseStringWrinkle from "../../organisms/DetailPurseStringWrinkle";
import DetailMarionetteLine from "../../organisms/DetailMarionetteLine";
import DetailDoubleChin from "../../organisms/DetailDoubleChin";
import DetailLowerJawLine from "../../organisms/DetailLowerJawLine";
import QRCodeSection from "../../molecules/QRCodeSection";


const ReportV = forwardRef(({data, avatarMode = false, ...props}, ref) => {
    const reportRef = useRef();

    // 청소년 사용자를 위한 이미지 마스킹 처리 + 아바타 모드 처리
    const processedData = useMemo(() => {
        return processReportImagesForJunior(data, avatarMode);
    }, [data, avatarMode]);

    ChartJS.register(ArcElement, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler, CategoryScale, LinearScale, PointElement, LineElement, Title, BarElement);

    const externalTooltipHandler = (context) => {
        const { chart, tooltip } = context;
        const point = tooltip?.dataPoints?.[0]?.raw ?? {};
        const dataPoint = tooltip.dataPoints[0];
        if (dataPoint.dataIndex == dataPoint.dataset.data.length - 1) {
            let tooltipEl = chart.canvas.parentNode.querySelector(`.chart-tooltip`);

            if (!tooltipEl) {
                const position = tooltip._active[0]?.element;

                tooltipEl = document.createElement("div");
                tooltipEl.classList.add("chart-tooltip");
                tooltipEl.style.left = position.x + "px";
                tooltipEl.style.top = position.y + "px";

                const body = point;
                const text = document.createTextNode(body);
                const span = document.createElement("span");
                span.innerHTML = "점";
                tooltipEl.appendChild(text);
                tooltipEl.appendChild(span);
                chart.canvas.parentNode.appendChild(tooltipEl);
            }
        }
    };
    const onChartReady = (e) => {
        if (e.initial && e.numSteps === e.currentStep) {
            e.chart.config["_config"].data.datasets.map(({ data }, datasetIndex) =>
                data.map(({ showTooltip }, index) => {
                    e.chart.tooltip.setActiveElements([
                        {
                            datasetIndex,
                            index,
                        },
                    ]);
                })
            );
        }
    };
    const dataLabelsOption = {
        display: function (context) {
            if (context.dataIndex != 0) {
                if (context.dataset.data.length - 1 != context.dataIndex) {
                    return true;
                }
            }
            return false;
        },
        offset: -2,
        anchor: "end",
        align: "end",
    };
    const comprehensiveInformationChartOptions = {
        responsive: false,
        animation: {
            duration: 0.1,
            onComplete: onChartReady,
        },
        plugins: {
            datalabels: dataLabelsOption,
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
                position: "average",
                animation: false,
                external: externalTooltipHandler,
            },
        },
        scales: {
            y: {
                grid: {
                    color: "rgba(205, 205, 205, 1)",
                    drawTicks: false,
                    lineWidth: 0.5,
                },
                ticks: {
                    display: false,
                    stepSize: 20,
                    font: {
                        family: "Wanted Sans Variable",
                    },
                },
                min: 0,
                max: 110,
            },
            x: {
                ticks: {
                    autoSkip: false,
                    font: {
                        family: "Wanted Sans Variable",
                        size: 8,
                    },
                    callback: function (val, idx, ticks) {
                        
                        if (idx != 0) {
                            // 데이터 있을 때만 레이블 노출
                            const data = this.chart.data.datasets[0].data[idx];
                            if (data) {
                                return this.chart.data.labels[idx];
                            }
                        }
                    },
                },
                beginAtZero: true,
                grid: {
                    display: false,
                },
                border: {
                    dash: [2, 4],
                },
            },
        },
        layout: {
            padding: {
                top: 21,
                right: 21,
            },
        },
    };
    function progress(per) {
        const RADIUS = 47;
        const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
        const progress = per / 100;
        const dashoffset = CIRCUMFERENCE * (1 - progress);

        return {
            dashoffset: dashoffset * -1,
            strokeDasharray: CIRCUMFERENCE,
        };
    }

    useEffect(() => {
        const circleProgress = document.querySelectorAll(".half-circle-progress-wrap");
        circleProgress.forEach((el) => {
            const bar = el.querySelector(".bar");
            const attr = progress(el.getAttribute("data-progress"));
            bar.style.strokeDasharray = attr.strokeDasharray;
            bar.style.strokeDashoffset = attr.dashoffset;
        });
    }, []);

    return (
        <div className="Report" ref={reportRef}>
            {/* page */}
            <section className="page page1">
                <div className="wrap">
                    <Cover/>
                </div>
            </section>
            {/* page */}
            {/* page */}
            <section className="page page2">
                <div className="wrap">
                    {/* (S)user-info-box */}
                    <UserInfo data={{
                        user_name: processedData.userName,
                        birth: processedData.birth,
                        gender: processedData.gender,
                        phone: processedData.phone,
                        center_name: processedData.centerName,
                        report_date: processedData.reportDate,
                        report_id: processedData.rid,
                        profile_img: processedData.imgList.image_cross_polar_front,
                    }} />
                    {/* (E)user-info-box */}

                    {/* (S) content-wrap */}
                    <div className="content-wrap">
                        <div className="row-wrap">
                            {/* 피부 노화정도 */}
                            <SkinAging data={{
                                skin_aging: {
                                    level: processedData.skinAging.level,
                                    score: processedData.skinAging.score,
                                    factor_list: processedData.skinAging.factorList
                                }
                            }}/>
                            {/* 피부 노화정도 */}

                            {/* 피부 트러블 */}
                            <SkinTrouble data={{
                                pore:{
                                    score: processedData.pore.score
                                },
                                blackhead: {
                                    score: processedData.blackhead.score, 
                                }, 
                                black_rim_of_eye: {
                                    score: processedData.blackRimOfEye.score
                                },
                                acne: {
                                    score: processedData.acne.score
                                },
                                skin_glow: {
                                    score: processedData.skinGlow.score,
                                },
                                speckle: {
                                    score: processedData.speckle.score
                                },
                            }}/>
                            {/* 피부 트러블 */}
                        </div>

                        {/* 피부톤과 특성 */}
                        <article className="skin-tone">
                            <div className="h2">피부톤과 특성</div>
                            <p className="desc">개인의 피부 색상과 색조를 나타내는 것이며, 각 톤은 고유한 특성을 가집니다. 색조멜라닌 수준, 피부민감도, 태닝 및 화상에 대한 반응 등이 있습니다.</p>
                            <p className="desc tip">적절한 색조 선택: 각 개인의 피부톤에 따라 어울리는 특정 색상이 있습니다. 이를 고려하여 메이크업, 의류 및 액세서리를 선택할 때 도움이 될 수 있습니다.</p>

                            {/* 피부톤 */}
                            <SkinTone 
                                data={processedData.skin} 
                            />
                            {/* 피부톤 */}

                            <div className="row-wrap">
                                {/* 피부 타입, 나이 */}
                                <div className="column-wrap skin-type-wrap">
                                    <section>
                                        <div className="body2">피부타입</div>
                                        <div className="h3">
                                            {processedData.skin.skinType == "1" && "건성"}
                                            {processedData.skin.skinType == "2" && "중성"}
                                            {processedData.skin.skinType == "3" && "지성"}
                                            {processedData.skin.skinType == "4" && "복합성"}
                                        </div>
                                    </section>
                                    <section>
                                        <div className="body2">피부나이</div>
                                        <div className="h3">{processedData.skin.skinAge}살</div>
                                        <p className="caption">
                                            실제나이보다 
                                            {
                                            processedData.skin.ageDiff > 0 
                                            ?
                                            " " + processedData.skin.ageDiff + "세 많음"
                                            : 
                                            " " + Math.abs(processedData.skin.ageDiff) + "세 적음"
                                            }
                                        </p>
                                    </section>
                                </div>
                                {/* 피부 타입, 나이 */}

                                <CharacteristicList data={{
                                    skin: {
                                        t_score: processedData.skin.tScore,
                                        chin_score: processedData.skin.chinScore,
                                        cheek_score: processedData.skin.cheekScore,
                                    }
                                }} 
                                />
                            </div>

                            <p className="desc info">그래프가 길수록 유분이 많고 짧을수록 유분이 적다는 것을 의미합니다.</p>
                        </article>
                        {/* 피부톤과 특성 */}
                    </div>
                    {/* (E) content-wrap */}
                </div>
            </section>
            {/* page */}
            {/* page */}
            <section className="page page3">
                <div className="wrap">
                    <div className="content-wrap">
                        <article className="comprehensive-information">
                            <div className="h2">종합 측정 정보</div>
                            <p className="body3">양호와 유지는 정상범위에 가까우며, 관심과 주의는 관리가 필요한 것을 나타냅니다.</p>

                            {
                                ["pore", "blackhead", "speckle", "wrinkle", "acne", "sensitive", "blackRimOfEye"].map((el,idx) => {
                                    let type = "";
                                    switch (el) {
                                        case "pore":
                                            type = "모공"
                                            break;
                                        case "blackhead":
                                            type = "블랙헤드"
                                            break;
                                        case "speckle":
                                            type = "색소스팟"
                                            break;
                                        case "wrinkle":
                                            type = "주름"
                                            break;
                                        case "acne":
                                            type = "포르피린"
                                            break;
                                        case "sensitive":
                                            type = "민감도"
                                            break;
                                        case "blackRimOfEye":
                                            type = "다크서클"
                                            break;
                                    }
                                    const dates =[""];
                                    const scores =[50];
                                    data[el]["beforeScore"].map(el2 => {
                                        dates.push(moment(el2.reportDate).format("YY.MM.DD"));
                                        scores.push(el2.score);
                                    });

                                    if (dates.length < 6) {
                                        Array(6 - dates.length).fill("0").map(el => {
                                            dates.push("");
                                        })
                                    }

                                    return (
                                        <MeasurementInfo 
                                            key={"info_"+el+"_"+idx}
                                            icn={process.env.PUBLIC_URL + "/image/icn_"+el+".png"} 
                                            name={type}
                                            data={data[el]}
                                        />
                                    )
                                })
                            }
                        </article>
                    </div>
                </div>
            </section>
            {/* page */}

            {/* =========== 측정 결과 상세 ========= */}
            <>
            {/* 광채 측정결과 */}
            <section className="page page4">
                <div className="wrap">
                    <div className="content-wrap">
                        <DetailSkinGlow
                            data={processedData.skinGlow}
                            img={processedData.imgList}
                            isJuniorUser={processedData.isJuniorUser}
                        />
                    </div>
                </div>
            </section>
            {/* 광채 측정결과 */}
            {/* 모공, 블랙헤드 측정결과 */}
            <section className="page page5">
                <div className="wrap">
                    <div className="content-wrap">
                        {/* 블랙헤드 */}
                        <DetailBlackhead
                            data={processedData.blackhead}
                            img={processedData.imgList}
                        />
                        {/* 블랙헤드 */}
                        {/* 모공 */}
                        <DetailPore
                            data={processedData.pore}
                            img={processedData.imgList}
                        />
                        {/* 모공 */}
                    </div>
                </div>
            </section>
            {/* 모공, 블랙헤드 측정결과 */}
            {/* 민감도, 색소스팟 측정결과 */}
            <section className="page page6">
                <div className="wrap">
                    <div className="content-wrap">
                        {/* 색소스팟 */}
                        <DetailSpeckle
                            data={processedData.speckle}
                            img={processedData.imgList}
                        />
                        {/* 색소스팟 */}
                        {/* 민감도 */}
                        <DetailSensitive
                            data={processedData.sensitive}
                            img={processedData.imgList}
                        />
                        {/* 민감도 */}
                    </div>
                </div>
            </section>
            {/* 민감도, 색소스팟 측정결과 */}
            {/* 포르피린, 이마 주름 측정결과 */}
            <section className="page page7">
                <div className="wrap">
                    <div className="content-wrap">
                        {/* 포르피린 */}
                        <DetailAcne
                            data={processedData.acne}
                            img={processedData.imgList}
                        />
                        {/* 포르피린 */}

                        {/* 이마 주름 */}
                        <DetailForeheadLine 
                            data={processedData.wrinkleType8}
                            img={processedData.imgList}
                        />
                        {/* 이마 주름 */}
                    </div>
                </div>
            </section>
            {/* 포르피린, 이마 주름 측정결과 */}
            {/* 미간 주름, 다크서클 측정결과 */}
            <section className="page page8">
                <div className="wrap">
                    <div className="content-wrap">
                        {/* 미간 주름 */}
                        <DetailFrownLine 
                            data={processedData.wrinkleType128} 
                            img={processedData.imgList}
                        />
                        {/* 다크서클 */}
                        <DetailDarkCircle 
                            data={processedData.blackRimOfEye}
                            img={processedData.imgList}
                        />
                    </div>
                </div>
            </section>
            {/* 미간 주름, 다크서클 측정결과 */}
            {/* 눈밑지방, 눈물고랑 측정결과 */}
            <section className="page page9">
                <div className="wrap">
                    <div className="content-wrap">
                        <DetailUnderEyeArea 
                            data={processedData.eyeBags} 
                            img={processedData.imgList}
                        />

                        <DetailTearTrough 
                            data={processedData.wrinkleType256} 
                            img={processedData.imgList}
                        />
                    </div>
                </div>
            </section>
            {/* 눈밑지방, 눈물고랑 측정결과 */}
            {/* 안검하수, 눈밑 잔주름 측정결과 */}
            <section className="page page10">
                <div className="wrap">
                    <div className="content-wrap">
                        <DetailPtosis 
                            data={processedData.eyeSagging}
                            img={processedData.imgList}
                        />

                        <DetailWrinkleUnderEye 
                            data={processedData.wrinkleType2}
                            img={processedData.imgList}
                        />
                    </div>
                </div>
            </section>
            {/* 안검하수, 눈밑 잔주름 측정결과 */}
            {/* 눈꼬리 주름, 애플존 측정결과 */}
            <section className="page page11">
                <div className="wrap">
                    <div className="content-wrap">
                        <DetailCrowFeet 
                            data={processedData.wrinkleType1} 
                            img={processedData.imgList}
                        />

                        <DetailAppleZone 
                            data={processedData.appleZone}
                            img={processedData.imgList}
                        />
                    </div>
                </div>
            </section>
            {/* 눈꼬리 주름, 애플존 측정결과 */}
            {/* 팔자주름, 마리오네트 주름 측정결과 */}
            <section className="page page12">
                <div className="wrap">
                    <div className="content-wrap">
                        <DetailPurseStringWrinkle 
                            data={processedData.wrinkleType16} 
                            img={processedData.imgList}
                        />

                        <DetailMarionetteLine 
                            data={processedData.wrinkleType64} 
                            img={processedData.imgList}
                        />
                    </div>
                </div>
            </section>
            {/* 팔자주름, 마리오네트 주름 측정결과 */}
            {/* 이중턱, 아래턱 라인 측정결과 */}
            <section className="page page13">
                <div className="wrap">
                    <div className="content-wrap">
                        <DetailDoubleChin 
                            data={processedData.doubleChin} 
                            img={processedData.imgList}
                        />

                        <DetailLowerJawLine 
                            data={processedData.jawLine} 
                            img={processedData.imgList}
                        />
                    </div>
                </div>
            </section>
            {/* 이중턱, 아래턱 라인 측정결과 */}
            </>
            {/* =========== 측정 결과 상세 ========= */}

            {/* 종합 측정 정보 */}
            <section className="page page14">
                <div className="wrap">
                    <div className="content-wrap">
                        <article className="comprehensive-information">
                            <div className="h2">종합 측정 정보</div>

                            {
                                ["pore", "blackhead", "speckle", "wrinkle", "acne", "sensitive", "blackRimOfEye"].map((el,idx) => {
                                    let type = "";
                                    switch (el) {
                                        case "pore":
                                            type = "모공"
                                            break;
                                        case "blackhead":
                                            type = "블랙헤드"
                                            break;
                                        case "speckle":
                                            type = "색소스팟"
                                            break;
                                        case "wrinkle":
                                            type = "주름"
                                            break;
                                        case "acne":
                                            type = "포르피린"
                                            break;
                                        case "sensitive":
                                            type = "민감도"
                                            break;
                                        case "blackRimOfEye":
                                            type = "다크서클"
                                            break;
                                    }
                                    const dates =[""];
                                    const scores =[50];
                                    [...data[el]["beforeScore"]].reverse().map(el2 => {
                                        dates.push(moment(el2.reportDate).format("YY.MM.DD"));
                                        scores.push(el2.score);
                                    });

                                    if (dates.length < 6) {
                                        Array(6 - dates.length).fill("0").map(el => {
                                            dates.push("");
                                        })
                                    }

                                    return (
                                        <section key={"werfdsgxvasd"+idx}>
                                            <div className="data-wrap type-02">
                                                <div className="left">
                                                    <div className="icn-title">
                                                        <img className="icon" src={process.env.PUBLIC_URL + "/image/icn_"+el+".png"} />
                                                        <div className="h3">{type}</div>
                                                    </div>
                                                    <p className="body2">
                                                        {data[el]['analysisResult2']}
                                                    </p>
                                                </div>

                                                <div className="chart-wrap">
                                                    <Line
                                                        width={212}
                                                        height={95}
                                                        plugins={[ChartDataLabels]}
                                                        options={comprehensiveInformationChartOptions}
                                                        data={{
                                                            labels: dates,
                                                            datasets: [
                                                                {
                                                                    fill: true,
                                                                    label: "Dataset 2",
                                                                    data: scores,
                                                                    borderColor: "rgb(53, 162, 235)",
                                                                    backgroundColor: "rgba(53, 162, 235, 0.5)",
                                                                    borderWidth: 1,
                                                                    borderColor: "rgba(28, 195, 145, 1)",
                                                                    backgroundColor: (context) => {
                                                                        const ctx = context.chart.ctx;
                                                                        const gradient = ctx.createLinearGradient(0, 0, 0, 60);
                                                                        gradient.addColorStop(0, "rgba(28, 195, 145, 0.3)");
                                                                        gradient.addColorStop(1, "rgba(28, 195, 145, 0.1)");
                                                                        return gradient;
                                                                    },
                                                                    pointRadius: 1.5,
                                                                    pointBorderColor: "rgba(28, 195, 145, 1)",
                                                                    pointBorderWidth: 0,
                                                                    pointHoverRadius: 1.5,
                                                                    pointBackgroundColor: function (context) {
                                                                        if (context.dataIndex == 0) {
                                                                            return "transparent";
                                                                        }
                                                                        if (context.dataIndex == context.dataset.data.length - 1) {
                                                                            return "#000";
                                                                        }
                                                                        return "rgba(28, 195, 145, 1)";
                                                                    },
                                                                },
                                                            ],
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </section>
                                    )
                                })
                            }
                        </article>
                    </div>
                </div>
            </section>
            {/* 종합 측정 정보 */}
            {/* 피부트러블 측정 결과 변화 수치 */}
            <section className="page page15">
                <div className="wrap">
                    <div className="content-wrap">
                        <article className="trouble-change-level">
                            <div className="h2">피부 총평</div>

                            <div className="bar-chart-wrap">
                                <div className="bg"></div>
                                <div className="bg"></div>
                                <div className="bg"></div>
                                <div className="bg"></div>
                                <div className="bg"></div>

                                <div className="bar-chart">
                                    {
                                        ["pore", "blackhead", "speckle", "wrinkle", "acne", "sensitive", "blackRimOfEye"].map((el) => {
                                            let type = "";
                                            switch (el) {
                                                case "pore":
                                                    type = "모공"
                                                    break;
                                                case "blackhead":
                                                    type = "블랙헤드"
                                                    break;
                                                case "speckle":
                                                    type = "색소스팟"
                                                    break;
                                                case "wrinkle":
                                                    type = "주름"
                                                    break;
                                                case "acne":
                                                    type = "포르피린"
                                                    break;
                                                case "sensitive":
                                                    type = "민감도"
                                                    break;
                                                case "blackRimOfEye":
                                                    type = "다크서클"
                                                    break;
                                            }
                                            const barData = [];
                                            data[el]["beforeScore"].map((el2, idx) => {
                                                const currentIdx = data[el]["beforeScore"].length - (idx + 1);
                                                const currentEl = data[el]["beforeScore"][currentIdx];
                                                if (idx < 3) {
                                                    barData.push({
                                                        score: currentEl.score,
                                                        date: moment(currentEl.reportDate).format("YY.MM.DD")
                                                    })
                                                }
                                            });

                                            return (
                                                <div className="group" key={el}>
                                                    {
                                                        barData.map((val2, idx2) => {
                                                            return(
                                                                <div className="bar" key={idx2}>
                                                                    <span className="date">{val2.date}</span>
                                                                    <div className={barData.length - 1 == idx2 ? "value point" : "value"} style={{height: val2.score + "%"}}>
                                                                        <span className="label">{val2.score}점</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    }

                                                    <div className="label">{type}</div>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            </div>

                        </article>
                        <article className={`general-status-review ${processedData.overallReview?.isJunior ? 'junior-version' : 'normal-version'}`}>
                            <div>
                                {/* QR Code for Junior Users (ages 0-19) - Positioned BEFORE the overall review text */}
                                {processedData.overallReview?.isJunior && (
                                    <QRCodeSection 
                                        url={processedData.overallReview.qrCodeAddress}
                                        age={processedData.overallReview.userAge}
                                    />
                                )}
                                
                                <p className="body2">
                                    {
                                        // Junior version compatibility: handle both string and object formats
                                        (typeof processedData.overallReview === 'string' ? processedData.overallReview : processedData.overallReview?.comment || '')
                                            .split(/\n/).map((el, idx) => {
                                                if (idx == 0) {
                                                    return el
                                                } else {
                                                    return (<><br/>{ el }</>)
                                                }
                                            })
                                    }
                                </p>
                            </div>
                            <p className="desc info">측정 전일에 과도한 음주, 호르몬의 변화와 측정 시 세안을 안했을 경우 측정 결과가 상이할 수 있습니다.</p>
                        </article>
                    </div>
                </div>
            </section>
            {/* 피부트러블 측정 결과 변화 수치 */}
        </div>
    );
});

export default ReportV;
